import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { TopCustomerProps, TopProductProps, TopReportProps } from "../pages/HomePage";

interface RecentlySearchedItems {
  recentlySearchedCustomers: TopCustomerProps[];
  recentlySearchedProducts: TopProductProps[];
  recentlySearchedReports: TopReportProps[];
}

// Define a service using a base URL and expected endpoints
export const commonAPI = createApi({
  reducerPath: "common",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["recentItems"],
  endpoints: (build) => ({
    getFilters: build.query({
      query: () => "common/getFilterData"
    }),
    getRecentlySearchedItems: build.query<RecentlySearchedItems, { loginEmail: string; loginName: string }>({
      query: ({ loginEmail, loginName }) => ({
        url: `common/getRecentlySearchedItems?LoginEmail=${loginEmail}&LoginName=${loginName}`,
        method: "GET"
      }),
      providesTags: ["recentItems"]
    }),
    // This mutation inserts a recently searched customer record.
    // Takes an object with LoginName, LoginEmail, TenantId, and TenantName as input.
    // Sends a POST request to "customer/insertRecentlySearchedCustomer" with the input data.
    // Returns a boolean to indicate success or failure.
    insertRecentlySearchedCustomer: build.mutation<
      boolean,
      { LoginName: string; LoginEmail: string; TenantId: string; TenantName: string }
    >({
      query: (body) => ({
        url: "customer/insertRecentlySearchedCustomer",
        method: "POST",
        body
      }),
      invalidatesTags: ["recentItems"]
    }),
    // This mutation inserts a recently searched product record.
    // Takes an object with LoginName, LoginEmail, ProductId, and ProductName as input.
    // Sends a POST request to "product/insertRecentlySearchedProduct" with the input data.
    // Returns a boolean to indicate success or failure.
    insertRecentlySearchedProduct: build.mutation<
      boolean,
      { LoginName: string; LoginEmail: string; ProductId: string; ProductName: string }
    >({
      query: (body) => ({
        url: "product/insertRecentlySearchedProduct",
        method: "POST",
        body
      }),
      invalidatesTags: ["recentItems"]
    }),
    // This mutation inserts a recently searched report catalog record.
    // Takes an object with LoginName, LoginEmail, ReportId, and Report as input.
    // Sends a POST request to "reportcatalog/insertRecentlySearchedReport" with the input data.
    // Returns a boolean to indicate success or failure.
    insertRecentlySearchedReport: build.mutation<
      void,
      { LoginName: string; LoginEmail: string; ReportId: string; Report: string }
    >({
      query: (body) => ({
        url: "reportcatalog/insertRecentlySearchedReport",
        method: "POST",
        body
      }),
      invalidatesTags: ["recentItems"]
    }),
    GetCountryCodeAndKpis: build.query({
      query: () => "common/getCountryCodeAndKpis"
    }),
    getAnnouncements: build.query({
      query: () => "feedback/getAnnouncements"
    }),
    saveFeedback: build.mutation({
      query(body) {
        return {
          url: "feedback/saveFeedback",
          method: "POST",
          body
        };
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// Auto-generated based on the defined endpoints
export const {
  useGetFiltersQuery,
  useGetRecentlySearchedItemsQuery,
  useInsertRecentlySearchedCustomerMutation,
  useInsertRecentlySearchedProductMutation,
  useInsertRecentlySearchedReportMutation,
  useGetCountryCodeAndKpisQuery,
  useGetAnnouncementsQuery,
  useSaveFeedbackMutation
} = commonAPI;
